import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Badge, Button, Space, Tooltip } from 'antd';
import moment from 'moment';
import { FileArrowUp, FileDotted } from 'phosphor-react';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FULL_DATE_TIME_FORMAT,
  LIST_TYPES,
  MODULES,
  ORDER,
  PAGE_TYPE_LABELS,
  PAGE_TYPES,
  PAGES_SORT_FIELD,
  ROUTES,
  STATUS_TYPES,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../common/constants';
import { modalContext } from '../../components/AppComponentContainer';
import FilterBar, { useFilterBar } from '../../components/FilterBar';
import PageHeader from '../../components/PageHeader';
import PageList from '../../components/PageList';
import useCheckPermission from '../../hooks/useCheckPermission';
import {
  DELETE_PAGE_TEMPLATE,
  UPDATE_PAGE_TEMPLATE_STATUS
} from './graphql/Mutations';
import { GET_PAGE_TEMPLATES } from './graphql/Queries';

// Commented for future use
// const TYPE_FILTER_OPTIONS = [
//   { label: 'All Types', value: '' },
//   { label: 'Video', value: PAGE_TYPES.VIDEO },
//   { label: 'Podcast', value: PAGE_TYPES.PODCAST }
// ];

const variablesSelector = ({
  limit,
  offset,
  search,
  sortField,
  sortOrder
}) => ({
  filter: {
    limit,
    skip: offset,
    search
  },
  sort: {
    sortOn: sortField,
    sortBy: sortOrder
  }
});

const dataSelector = ({ pageTemplates }) => ({
  data: pageTemplates?.pages || [],
  count: pageTemplates?.count || 0
});

const Status = ({ status }) => (
  <div>
    {status === STATUS_TYPES.DRAFT && (
      <Badge className="status" count={status} color="#5E5957" />
    )}
    {status === STATUS_TYPES.PUBLISHED && (
      <Badge className="status" count={status} color="#52c41a" />
    )}
    {status === STATUS_TYPES.UNPUBLISHED && (
      <Badge className="status" count={status} />
    )}
  </div>
);

const Action = ({
  id,
  status,
  isDefault,
  removeItem,
  refresh,
  isAddEditAllowed,
  isDeleteAllowed
}) => {
  const [deletePageTemplate] = useMutation(DELETE_PAGE_TEMPLATE);
  const [updateTemplateStatus] = useMutation(UPDATE_PAGE_TEMPLATE_STATUS);

  const newStatus =
    status === STATUS_TYPES.DRAFT ? STATUS_TYPES.PUBLISHED : STATUS_TYPES.DRAFT;

  const statusTitle = {
    [STATUS_TYPES.PUBLISHED]: 'Publish this template',
    [STATUS_TYPES.DRAFT]: 'Draft this template'
  }[newStatus];

  const StatusIcon = {
    [STATUS_TYPES.PUBLISHED]: FileArrowUp,
    [STATUS_TYPES.DRAFT]: FileDotted
  }[newStatus];

  const isDeleteDisabled = isDefault && status === STATUS_TYPES.PUBLISHED;

  const handleUpdateStatus = () => {
    modalContext?.confirm({
      title: `Are you sure you want to ${statusTitle?.toLowerCase()}?`,
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      okType: 'primary',
      onOk() {
        updateTemplateStatus({ variables: { id, status: newStatus } })
          .then(({ errors }) => {
            if (refresh && !errors) refresh();
          })
          .catch();
      }
    });
  };

  const handleDelete = () => {
    if (isDeleteDisabled) return;
    modalContext?.confirm({
      title: 'Are you sure you want to delete this template?',
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        deletePageTemplate({ variables: { where: { id } } })
          .then(({ errors }) => {
            if (removeItem && !errors) removeItem(id);
          })
          .catch();
      }
    });
  };

  return (
    <div className="d-flex align-center">
      {isAddEditAllowed && (
        <>
          <Tooltip title={statusTitle}>
            <Button
              className="text-btn d-flex align-center justify-center"
              type="text"
              onClick={handleUpdateStatus}
            >
              <StatusIcon size={22} />
            </Button>
          </Tooltip>
          <Tooltip title="Edit template">
            <Link
              to={`${ROUTES.PAGE_TEMPLATES}/${id}/edit`}
              className="text-btn black-btn"
              type="text"
              disabled={status === STATUS_TYPES.PUBLISHED}
              onClick={(e) => {
                if (status === STATUS_TYPES.PUBLISHED) {
                  e.preventDefault();
                }
              }}
            >
              <EditOutlined />
            </Link>
          </Tooltip>
        </>
      )}
      {isDeleteAllowed && (
        <Tooltip
          title={
            isDeleteDisabled
              ? 'Cannot delete a published default template'
              : 'Delete template'
          }
        >
          <Button
            disabled={isDeleteDisabled}
            className="text-btn"
            type="text"
            onClick={handleDelete}
          >
            <DeleteOutlined />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

const tableColumns = (
  { removeItem, refresh },
  isAddEditAllowed,
  isDeleteAllowed
) => [
  {
    title: 'Template Name',
    dataIndex: 'templateName',
    key: 'templateName',
    width: 200,
    render: (text, record) => (
      <div className="fs-12 text-secondary">{record.templateName}</div>
    )
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 120,
    render: (text) => (
      <Space size={4}>
        <span>{PAGE_TYPE_LABELS[text] ?? text}</span>
      </Space>
    )
  },
  {
    title: 'Last Modified',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 170,
    render: (_, record) => (
      <span>{moment(record?.updatedAt).format(FULL_DATE_TIME_FORMAT)}</span>
    )
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 110,
    render: (text) => <Status status={text} />
  },
  {
    title: 'Actions',
    dataIndex: 'id',
    width: 100,
    render: (_, record) => (
      <Action
        {...record}
        removeItem={removeItem}
        refresh={refresh}
        isAddEditAllowed={isAddEditAllowed}
        isDeleteAllowed={isDeleteAllowed}
      />
    )
  }
];

const PageTemplates = ({ history }) => {
  // commented for future use
  // const [typeFilter, setTypeFilter] = useState('');

  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.PAGE_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isDeleteAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.PAGE_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.DELETE]
    }
  ]);

  const filterProps = useFilterBar({
    defaultSortOptions: {
      options: PAGES_SORT_FIELD,
      order: ORDER[1].value,
      field: PAGES_SORT_FIELD[0].value
    }
  });

  const filters = useMemo(
    () => ({
      ...filterProps.filters,
      type: PAGE_TYPES.VIDEO
    }),
    [filterProps.filters]
  );

  const columns = useCallback(
    (props) =>
      tableColumns(props, isAddEditAllowed, isDeleteAllowed).filter(
        (item) => item !== false
      ),
    [isAddEditAllowed, isDeleteAllowed]
  );

  const handleShowModal = () => {
    history?.push(`${ROUTES.PAGE_TEMPLATES}/add`);
  };

  return (
    <>
      <PageHeader
        menu={MODULES?.UI_CONFIGURATION}
        handleShowModal={handleShowModal}
        isAddEditAllowed={isAddEditAllowed}
      />
      <FilterBar
        {...filterProps}
        SearchProps={{ placeholder: 'Search Templates' }}
        show={{ listModes: false }}
      >
        {/* <AntdSelect
          value={typeFilter}
          options={TYPE_FILTER_OPTIONS}
          onChange={setTypeFilter}
          style={{ minWidth: 130 }}
        /> */}
      </FilterBar>
      <PageList
        filters={filters}
        listMode={LIST_TYPES.TABLE}
        query={GET_PAGE_TEMPLATES}
        variablesSelector={variablesSelector}
        dataSelector={dataSelector}
        TableProps={{ columns }}
        limit={30}
      />
    </>
  );
};

export default PageTemplates;
